import {
  GAME_RESULT_VARIANT,
  GAME_SESSION_STATUS,
  LIVE_REACHED_END_RECOVERY_RESULT,
  type LiveReachedEndRecoveryResult,
} from '@/constants/maze';
import type { LiveGameResult } from '@/store/live';
import { useLiveStore } from '@/store/live';
import type { GameSessionResponse } from '@/types/api/game';
import { MazeGamePhase } from '@/types/maze-phase';
import type { StageId } from '@/types/stage-theme';
import { normalizeStageId } from '@/utils/stage/stage-utils';
import { paths } from '@app/router/routes';
import type { NavigateFunction } from 'react-router-dom';

import {
  delay,
  isTerminalGameStatus,
  LIVE_GAME_BOOTSTRAP_MIN_UI_MS,
  resolveRoundsProgress,
} from './maze-live-session-utils';

export type { LiveReachedEndRecoveryResult } from '@/constants/maze';

export interface LiveReachedEndRecoveryDeps {
  readonly cancelled: () => boolean;
  readonly startedAt: number;
  readonly stageId: StageId;
  readonly advanceRoundAfterReachingEnd: () => Promise<{
    nextMaze: unknown;
    endBoard: { roundScore: number };
    endGame: unknown;
  }>;
  readonly applyLiveSessionToHud: (data: GameSessionResponse) => void;
  readonly finishBootstrap: () => void;
  readonly markBootstrapFinishedForTerminalNav: () => void;
  readonly setLiveBootstrapUiReady: (ready: boolean) => void;
  readonly clearLiveSession: () => void;
  readonly navigate: NavigateFunction;
  readonly setResult: (payload: LiveGameResult) => void;
}

/**
 * Refresh recovery: status shows an active session with `maze.reachedEnd` while the client never
 * finished `next-board` → `end-board`. Reuses store `advanceRoundAfterReachingEnd` (same as goal).
 */
export async function runLiveReachedEndRecoveryIfNeeded(
  sessionData: GameSessionResponse,
  deps: LiveReachedEndRecoveryDeps,
): Promise<LiveReachedEndRecoveryResult> {
  if (sessionData.status !== GAME_SESSION_STATUS.STARTED || sessionData.maze?.reachedEnd !== true) {
    return LIVE_REACHED_END_RECOVERY_RESULT.NOT_NEEDED;
  }

  if (deps.cancelled()) {
    return LIVE_REACHED_END_RECOVERY_RESULT.HANDLED;
  }

  await deps.advanceRoundAfterReachingEnd();

  if (deps.cancelled()) {
    return LIVE_REACHED_END_RECOVERY_RESULT.HANDLED;
  }

  const after = useLiveStore.getState();

  if (after.phase === MazeGamePhase.WIN || after.phase === MazeGamePhase.LOSE) {
    deps.markBootstrapFinishedForTerminalNav();
    deps.setLiveBootstrapUiReady(true);
    return LIVE_REACHED_END_RECOVERY_RESULT.HANDLED;
  }

  if (!after.session) {
    if (!deps.cancelled()) {
      deps.clearLiveSession();
      deps.navigate(paths.home, { replace: true });
    }
    return LIVE_REACHED_END_RECOVERY_RESULT.HANDLED;
  }

  if (isTerminalGameStatus(after.session.status)) {
    const elapsed = Date.now() - deps.startedAt;
    await delay(Math.max(0, LIVE_GAME_BOOTSTRAP_MIN_UI_MS - elapsed));
    if (deps.cancelled()) {
      return LIVE_REACHED_END_RECOVERY_RESULT.HANDLED;
    }
    deps.markBootstrapFinishedForTerminalNav();
    const stage = normalizeStageId(deps.stageId);
    const { total, completed } = resolveRoundsProgress(after.session);
    const score = after.session.scoreboard.totalScore;
    const resolvedVariant =
      score === 0 ? GAME_RESULT_VARIANT.FAILURE : GAME_RESULT_VARIANT.SUCCESS;
    deps.setResult({
      stage,
      score,
      completed,
      total,
      variant: resolvedVariant,
    });
    deps.navigate(paths.results, { replace: true });
    return LIVE_REACHED_END_RECOVERY_RESULT.HANDLED;
  }

  deps.applyLiveSessionToHud(after.session);
  deps.finishBootstrap();
  return LIVE_REACHED_END_RECOVERY_RESULT.HANDLED;
}
